import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Zap, Package, ArrowRight } from "lucide-react";

function FounderCard({ f }) {
  const joined = f.joined ? new Date(f.joined).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "";
  return (
    <div data-testid={`founder-card-${f.number}`} className="rounded-2xl border border-neutral-800 bg-neutral-950/80 p-6 flex flex-col hover:border-[#FF3B30]/50 transition-colors">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#FF3B30]/15 border border-[#FF3B30]/40 text-[#FF3B30] font-black text-sm">
          #{f.number}
        </span>
        {f.picture ? (
          <img src={f.picture} alt="" className="h-10 w-10 rounded-full object-cover border border-neutral-700" />
        ) : (
          <span className="h-10 w-10 rounded-full bg-neutral-800 flex items-center justify-center font-black text-neutral-400">
            {(f.name || "?").charAt(0).toUpperCase()}
          </span>
        )}
      </div>
      <div className="mt-4 font-black tracking-tight text-lg">{f.name}</div>
      <div className="text-xs text-neutral-500 mt-1">Founding seller{joined ? ` · joined ${joined}` : ""}</div>
      <div className="mt-4 flex items-center justify-between text-xs">
        <span className="inline-flex items-center gap-1.5 text-neutral-400">
          <Package size={13} /> {f.products} product{f.products !== 1 ? "s" : ""}
        </span>
        {f.products > 0 && (
          <Link
            data-testid={`founder-shop-link-${f.number}`}
            to={`/discover?seller=${f.id}`}
            className="inline-flex items-center gap-1 text-[#FF3B30] font-bold hover:underline"
          >
            View shop <ArrowRight size={12} />
          </Link>
        )}
      </div>
    </div>
  );
}

export default function FoundersWall() {
  const [wall, setWall] = useState(null);
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    api.get("/founders/wall").then(({ data }) => setWall(data)).catch(() => setWall({ cap: 20, founders: [] }));
    api.get("/billing/founders-promo").then(({ data }) => setPromo(data)).catch(() => {});
  }, []);

  const founders = wall?.founders || [];
  const cap = wall?.cap ?? 20;
  const remaining = promo?.remaining ?? Math.max(0, cap - founders.length);

  return (
    <div data-testid="founders-wall-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
      <div className="max-w-2xl">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#FF3B30]/50 bg-[#FF3B30]/10 text-[#FF3B30] text-xs font-bold uppercase tracking-widest">
          <Zap size={14} /> The Founding {cap}
        </div>
        <h1 className="mt-5 font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl">Founder Wall</h1>
        <p className="mt-4 text-neutral-400">
          The first {cap} sellers who built VISMARKS with us — permanently spotlighted. Each founder claimed a
          numbered spot nobody else will ever hold.
        </p>
        {remaining > 0 && (
          <Link
            data-testid="founders-claim-spot"
            to="/register?role=seller"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white font-bold text-sm px-7 py-3 hover:bg-[#e0342a] transition-colors"
          >
            <Zap size={15} /> {remaining} spot{remaining !== 1 ? "s" : ""} left — claim yours
          </Link>
        )}
      </div>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {founders.map((f) => <FounderCard key={f.id} f={f} />)}
        {Array.from({ length: Math.max(0, cap - founders.length) }).map((_, i) => (
          <Link
            key={`open-${i}`}
            to="/register?role=seller"
            data-testid={`founder-open-slot-${founders.length + i + 1}`}
            className="rounded-2xl border border-dashed border-neutral-800 p-6 flex flex-col items-center justify-center text-center hover:border-[#FF3B30]/60 transition-colors group min-h-[180px]"
          >
            <span className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-neutral-700 text-neutral-500 font-black text-sm group-hover:border-[#FF3B30] group-hover:text-[#FF3B30] transition-colors">
              #{founders.length + i + 1}
            </span>
            <div className="mt-3 text-xs uppercase tracking-widest text-neutral-500 font-bold group-hover:text-white transition-colors">Available</div>
            <div className="mt-1 text-[11px] text-neutral-600">First month free · unlimited listings</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
